import { useState } from "react";
import { Check, Heart } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { buildPostAuthPath, POST_AUTH_REDIRECT_KEY } from "@/lib/authRedirect";
import { saveFollowIntent } from "@/lib/followIntent";
import { isValidEmail, submitNewsletterOptIn } from "@/lib/newsletter";
import { trackEvent } from "@/lib/analytics";
import { attachFollowAttribution, sendEvent, stashFollowSource } from "@/lib/firstPartyAnalytics";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  athleteId: string;
  athleteName: string;
  athleteSlug: string;
  firstName: string;
  isFollowing: boolean;
  onSaved: () => Promise<void>;
  /** Demo profiles show the real form and resolve locally — no account, follow or email. */
  demo?: boolean;
};

const FollowAthleteDialog = ({
  open,
  onOpenChange,
  athleteId,
  athleteName,
  athleteSlug,
  firstName: athleteFirstName,
  isFollowing,
  onSaved,
  demo = false,
}: Props) => {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email ?? "");
  const [firstName, setFirstName] = useState("");
  const [updatesConsent, setUpdatesConsent] = useState(false);
  const [newsletterConsent, setNewsletterConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const resetConsent = () => {
    setUpdatesConsent(false);
    setNewsletterConsent(false);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalizedEmail = (user?.email ?? email).trim();
    if (!isValidEmail(normalizedEmail)) return toast.error("Enter a valid email address.");
    if (!updatesConsent) return toast.error(`Consent is required for ${athleteFirstName}'s updates.`);

    if (demo) {
      await onSaved();
      resetConsent();
      onOpenChange(false);
      toast.success(`Demo profile: you'd now be in ${athleteFirstName}'s corner. Nothing was saved.`);
      return;
    }

    setSubmitting(true);
    const intent = {
      athleteId,
      athleteSlug,
      firstName: firstName.trim().slice(0, 80),
      updatesConsent: true as const,
      newsletterConsent,
    };

    if (newsletterConsent) {
      // Only ever called when the optional box is ticked. Fire-and-forget: a
      // newsletter problem must never block the follow.
      const fromWave = new URLSearchParams(window.location.search).get("from") === "wave";
      void submitNewsletterOptIn(normalizedEmail, {
        signupSource: "athlete_profile",
        campaign: fromWave ? "jersey_shore_wave" : null,
        athleteInterest: athleteSlug,
        entryPage: window.location.pathname,
      });
    }

    if (!user) {
      const nonce = crypto.randomUUID();
      saveFollowIntent({ ...intent, nonce });
      stashFollowSource(nonce, athleteId);
      const returnPath = `/athlete/${athleteSlug}`;
      window.localStorage.setItem(POST_AUTH_REDIRECT_KEY, returnPath);
      const { error } = await supabase.auth.signInWithOtp({
        email: normalizedEmail,
        options: {
          emailRedirectTo: `${window.location.origin}${buildPostAuthPath(returnPath, false)}`,
          shouldCreateUser: true,
          data: firstName.trim() ? { display_name: firstName.trim(), first_name: firstName.trim() } : undefined,
        },
      });
      setSubmitting(false);
      resetConsent();
      onOpenChange(false);
      toast.success(
        error
          ? "If this address can receive sign-in email, check your inbox and try again shortly."
          : "Check your inbox to finish following. The message is the same for new and existing fans.",
      );
      return;
    }

    const consentAt = new Date().toISOString();
    const profileChanges = {
      ...(firstName.trim() ? { first_name: firstName.trim(), display_name: firstName.trim() } : {}),
      ...(newsletterConsent ? { newsletter_opt_in: true, newsletter_consented_at: consentAt } : {}),
    };
    const [{ error: favoriteError }, { error: profileError }] = await Promise.all([
      supabase.from("athlete_favorites").upsert({
        user_id: user.id,
        athlete_id: athleteId,
        updates_opt_in: true,
        updates_consented_at: consentAt,
      }),
      supabase.from("profiles").update(profileChanges).eq("id", user.id),
    ]);
    setSubmitting(false);
    if (favoriteError || profileError) return toast.error("We couldn't save that preference. Please try again.");
    await onSaved();
    trackEvent("follow_click", { athlete: athleteSlug, following: true, updates_opt_in: true });
    sendEvent("follow_complete", { athleteId, metadata: { newsletter_opt_in: newsletterConsent } });
    attachFollowAttribution(athleteId);
    resetConsent();
    onOpenChange(false);
    toast.success(`You're in ${athleteFirstName}'s corner.`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase">Follow {athleteName}</DialogTitle>
          <DialogDescription>
            {demo ? "Fictional demo athlete. No sign-in email, account, newsletter subscription or follow is created." : `Get a password-free sign-in link and see ${athleteFirstName}'s published updates in My Corner.`}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="follow-email" className="text-sm font-semibold">Email address</label>
            <input
              id="follow-email"
              type="email"
              value={user?.email ?? email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={Boolean(user)}
              autoComplete="email"
              required
              maxLength={255}
              className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 disabled:opacity-70"
            />
          </div>
          <div>
            <label htmlFor="follow-first-name" className="text-sm font-semibold">First name <span className="font-normal text-muted-foreground">(optional)</span></label>
            <input
              id="follow-first-name"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              autoComplete="given-name"
              maxLength={80}
              className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2"
            />
          </div>
          <label className="flex items-start gap-3 text-sm leading-relaxed">
            <Checkbox checked={updatesConsent} onCheckedChange={(value) => setUpdatesConsent(value === true)} aria-required="true" />
            <span>
              Yes, I want to follow {athleteName} and receive her stories and updates from Say It In Sports.{" "}
              <strong>Required to follow.</strong>
            </span>
          </label>
          <label className="flex items-start gap-3 text-sm leading-relaxed">
            <Checkbox checked={newsletterConsent} onCheckedChange={(value) => setNewsletterConsent(value === true)} />
            <span>Also send me the weekly Say It In Sports newsletter—women's sports stories, business and voices worth knowing.</span>
          </label>
          <p className="text-xs text-muted-foreground">
            Follow with your email—no password required. Her updates appear in My Corner.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting || !updatesConsent}>
              {isFollowing ? <Check className="mr-2 h-4 w-4" /> : <Heart className="mr-2 h-4 w-4" />}
              {submitting ? "Saving…" : isFollowing ? "Update preferences" : `Follow ${athleteFirstName}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FollowAthleteDialog;